import { useCallback, useEffect, useRef, type RefObject } from 'react'
import { RESOLVE_GRADES } from '@/lib/grades'

const STEP_MS = 110

/**
 * Type that arrives pixelated and resolves, the way on-chain crystals does it:
 * step the element through Redaction's bitmap grades (100 → clean) by setting
 * data-grade, which the stylesheet maps to a face. Runs when the element first
 * enters view; `replay` runs it again (e.g. on hover). No-op under reduced motion.
 */
export function useResolve<T extends HTMLElement>(): { ref: RefObject<T | null>; replay: () => void } {
  const ref = useRef<T | null>(null)
  const timer = useRef<number | undefined>(undefined)

  const replay = useCallback(() => {
    const el = ref.current
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    window.clearTimeout(timer.current)
    let i = 0
    const tick = () => {
      const grade = RESOLVE_GRADES[i]
      if (grade === null) el.removeAttribute('data-grade')
      else el.setAttribute('data-grade', String(grade))
      i += 1
      if (i < RESOLVE_GRADES.length) timer.current = window.setTimeout(tick, STEP_MS)
    }
    tick()
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    // Markup starts coarse (data-grade="100"); without motion, show the clean face at once.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.removeAttribute('data-grade')
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect()
          // Let the grade fonts load first, or the first steps flash the fallback.
          void document.fonts.ready.then(replay)
        }
      },
      { threshold: 0.5 },
    )
    io.observe(el)
    return () => {
      io.disconnect()
      window.clearTimeout(timer.current)
    }
  }, [replay])

  return { ref, replay }
}
